<template>
  <main id="content" aria-labelledby="main-title">
    
    <article v-if="page" class="block relative">
      
      <ElementHeroPolicy v-if="page" :content="page" />

      <div class="px-4 sm:px-8 max-w-[800px] lg:max-w-[1100px] mx-auto lg:flex mb-16 md:mb-20 lg:mb-24 relative">

        <FadeIn class="hidden lg:block lg:w-[300px] lg:pr-8 relative">
          <ElementTableOfContents />
        </FadeIn>

        <div class="lg:w-[calc(100%-300px)] lg:pt-[90px]">
          <FadeIn>
            <!-- The outer loop provides the 'sectionIndex' for the ID and click handler -->
            <div v-for="(section, sectionIndex) in page?.dynamic" :key="section.id">
              
              <div v-if="section.collection === 'content'" class="prose">
                <div v-html="section.item.content" />
              </div>
              
              <div v-else-if="section.collection === 'content_tabs'" class="prose">
                
                <div v-html="section.item.content" />

                <!-- The ID uses the outer loop's 'sectionIndex' -->
                <div v-if="tabs(section.item).length" :id="'tabs-'+sectionIndex">

                  <div class="md:flex md:gap-x-2 md:mb-2">
                    <!-- The inner loop now uses 'tab' and 'tabIndex' to avoid conflicts -->
                    <!-- The click handler passes the outer loop's 'sectionIndex' -->
                    <span
                    v-for="(tab, tabIndex) in tabs(section.item)"
                    :key="tabIndex"
                    @click="tabClick(tab.title, sectionIndex)"
                    class="inline-block mr-2 mb-2 md:mr-0 md:mb-0 pr-1 pl-1 sm:pl-2 py-1 md:px-2 lg: rounded-lg cursor-pointer text-sm font-semibold md:text-base"
                    :class="{
                      'bg-[#d8da71]' : tab.title === 'Reporters',
                      'bg-[#f1cd52]' : tab.title === 'Authorised Client Users',
                      'bg-[#b4d0fb]' : tab.title === 'Website Users',
                    }"
                    >
                      <!-- The v-if now checks the state for the specific sectionIndex -->
                      {{ tab.title }} <Icon class="relative top-[3px]" v-if="selectedTabs[sectionIndex] === tab.title" name="iconoir:nav-arrow-down" /><Icon class="relative top-[3px]" v-else name="iconoir:nav-arrow-up" />
                    </span>
                  </div>

                  <!-- This loop is also corrected to use the clean 'tabs' function -->
                  <div
                  v-for="(tab, tabIndex) in tabs(section.item)"
                  :key="tabIndex"
                  class="hidden px-4 py-2 rounded-lg tabs"
                  :class="{
                    /* The class binding now checks the state for the specific sectionIndex */
                    '!block' : selectedTabs[sectionIndex] === tab.title,
                    'bg-[#eff0c6]' : tab.title === 'Reporters',
                    'bg-[#f9ebba]' : tab.title === 'Authorised Client Users',
                    'bg-[#e1ecfd]' : tab.title === 'Website Users',
                  }"
                  >
                    <h3 class="hidden">{{ tab.title }}</h3>
                    <div v-html="tab.content" />
                  </div>
                </div>

              </div>

              <div v-else-if="section.collection === 'component_block'">
                <component :is="'Policy'+section.item.component" />
              </div>

            </div>

          </FadeIn>
        </div>

      </div>
    </article>
  </main>
</template>

<script setup>
// Remount on every navigation (its own page key, since app.vue no longer sets a global
// one). This page renders ElementTableOfContents, which scans the rendered headings in
// onMounted; without a remount, policy→policy navigation would leave a stale table of
// contents (and the setup-scope 404 check would not re-run). See
// content-drafts/specs/remove-global-page-key.md.
definePageMeta({ key: route => route.fullPath })

// Publish gate: an unbuilt CMS page 404s until the next deploy prerenders it.
useRequirePrerendered()

const route = useRoute()
const { getItems } = useCms()

const tabClick = (tabTitle, sectionIndex) => {
  selectedTabs.value[sectionIndex] = tabTitle
}

const tabs = (item) => {
  if (!item || (!item.clients && !item.reporters && !item.website_users)) {
    return []
  }
  
  const tabList = [
    {
      title: 'Reporters',
      content: item.reporters
    },
    {
      title: 'Authorised Client Users',
      content: item.clients
    },
    {
      title: 'Website Users',
      content: item.website_users
    }
  ]

  return tabList.filter(tab => tab.content)
}

const fields = [
  'seo.*',
  'locale',
  'slug',
  'title',
  'subtitle',
  'status',
  'dynamic.id',
  'dynamic.collection',
  'dynamic.sort',
  'dynamic.item.*'
]

const currentLocale = computed(() => {
  return route.path.startsWith('/nz') ? 'nz' : 'au'
})

const { data, error } = await useAsyncData(
  `${currentLocale.value}-policies-${route.params.slug}`,
  () => getItems({
    collection: 'policy',
    params: {
      filter: {
        locale: { _contains: currentLocale.value },
        slug: { "_eq": route.params.slug },
        status: { _in: useRuntimeConfig().public.postStatus }
      },
      fields
    },
  })
)

// 404 only when the fetch SUCCEEDED and returned no policy. A fetch error (CMS 5xx /
// timeout) sets `error`; do not 404 a real policy over a transient blip. This page
// remounts per navigation (definePageMeta key above), so setup re-runs each nav — no
// client-nav watch needed.
if (!error.value && (!data.value || !data.value.length)) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
    fatal: true
  })
}

const page = data?.value[0]

const selectedTabs = ref({})

if (page?.dynamic) {
  page.dynamic.forEach((section, index) => {
    if (section.collection === 'content_tabs') {
      const availableTabs = tabs(section.item)
      if (availableTabs.length > 0) {
        selectedTabs.value[index] = availableTabs[0].title
      }
    }
  })
}

const seo = computed(() => {
  if (page?.seo) {
    return useSeo(page.seo, route.fullPath)
  }
  return {}
})

useHead(seo)
</script>
